import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiHome,
  FiDollarSign,
  FiMinusCircle,
  FiPercent,
  FiSettings,
  FiUsers,
  FiLogOut,
  FiX,
} from 'react-icons/fi';

/**
 * Sidebar - Main navigation sidebar for the maaser calculator app.
 * Supports RTL layout and responsive mobile toggling.
 *
 * @param {boolean} isOpen - Whether the sidebar is currently visible (mobile).
 * @param {function} toggleSidebar - Callback to toggle sidebar visibility.
 */
const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { user, logout } = useAuth();

  /** Navigation items available to all authenticated users. */
  const navItems = [
    { to: '/', label: 'דשבורד', icon: <FiHome /> },
    { to: '/incomes', label: 'הכנסות', icon: <FiDollarSign /> },
    { to: '/expenses', label: 'הוצאות', icon: <FiMinusCircle /> },
    { to: '/maaser', label: 'תשלומי מעשר', icon: <FiPercent /> },
    { to: '/settings', label: 'הגדרות', icon: <FiSettings /> },
  ];

  /** Handle logout action. */
  const handleLogout = () => {
    logout();
  };

  /** Derive the user's display initial from first_name. */
  const userInitial = user?.first_name ? user.first_name.charAt(0) : '?';

  /** Derive the user's full display name. */
  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.first_name || user?.username || '';

  /** Display role label in Hebrew. */
  const roleLabel = user?.is_admin ? 'מנהל' : 'משתמש';

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      {/* Mobile close button */}
      <button className="sidebar-close-btn" onClick={toggleSidebar} aria-label="סגור תפריט">
        <FiX />
      </button>

      {/* App branding */}
      <div className="sidebar-header">
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'linear-gradient(135deg, #1a6b5a, #2a9d63)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 10,
          boxShadow: '0 2px 10px rgba(26, 107, 90, 0.3)'
        }}>
          <span style={{ color: '#fff', fontWeight: 700, fontFamily: 'Rubik, sans-serif', fontSize: '1.1rem' }}>מ</span>
        </div>
        <h1 className="sidebar-title">מחשבון מעשר</h1>
        <p className="sidebar-subtitle">ניהול הכנסות ומעשרות</p>
      </div>

      {/* Main navigation */}
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {navItems.map((item) => (
            <li key={item.to} className="nav-item">
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={toggleSidebar}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Admin section - visible only to admin users */}
        {user?.is_admin && (
          <div className="nav-section">
            <h3 className="nav-section-title">ניהול</h3>
            <ul className="nav-list">
              <li className="nav-item">
                <NavLink
                  to="/admin"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  onClick={toggleSidebar}
                >
                  <span className="nav-icon"><FiUsers /></span>
                  <span className="nav-label">ניהול משתמשים</span>
                </NavLink>
              </li>
            </ul>
          </div>
        )}
      </nav>

      {/* Sidebar footer with user info and logout */}
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">{userInitial}</div>
          <div className="user-details">
            <span className="user-name">{displayName}</span>
            <span className="user-role">{roleLabel}</span>
          </div>
        </div>
        <button className="logout-btn" onClick={handleLogout} aria-label="התנתקות">
          <FiLogOut />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
